import { ref, computed } from 'vue';
import { useModal, useRouter, useTranslation } from '@/composables/plugins';
import type { ToBeFixed } from '@cutr/constants/utility-types';
import type { Customer } from '@cutr/constants/order';
import { useActiveUserStore } from '@/stores/user';
import { useOrderStore } from '@/stores/order';

/**
 * Composable for handling authentication logic in orders
 * Primarily used by the Modals component
 */
// State
const authenticationDismissed = ref(false);
const onAfterAuthenticateSuccess = ref<(() => void) | null>(null);
const prevRoute = ref<ToBeFixed | null>(null);

export function useOrderAuthentication() {
  const modal = useModal();
  const router = useRouter();
  const { t } = useTranslation();
  const activeUserStore = useActiveUserStore();
  const orderStore = useOrderStore();

  // Computed
  const requireBusinessOnSignup = computed(() => {
    const fulfilmentCountry =
      orderStore.currentOrder?.customerFulfilmentAddress?.country ||
      orderStore.addresses.find(
        ({ id }) => id === orderStore.currentOrder?.customerFulfilmentAddressId
      )?.country ||
      orderStore.deliveryAddress.country;

    return fulfilmentCountry && !['NL', 'DE'].includes(fulfilmentCountry);
  });

  // Methods
  function authenticateUserIfNecessary(
    hasFiles: boolean,
    cutlistId: string | null
  ) {
    const isPlaywrightE2ETest = navigator.userAgent.startsWith('e2e-test-pw');

    if (
      !modal.isOpen('authenticate') &&
      !isPlaywrightE2ETest &&
      !activeUserStore.isLoggedIn &&
      (hasFiles || cutlistId) &&
      !authenticationDismissed.value
    ) {
      showAuthenticateModal({
        showDismiss: true,
        headerTextKey: 'order:modals.authenticate.header.saveYourWork',
        bodyTextKey: 'order:modals.authenticate.body.continueQuote',
      });
    }
  }

  function showAuthenticateModal(context: ToBeFixed, next?: () => void) {
    onAfterAuthenticateSuccess.value = next ?? null;

    const { headerTextKey, bodyTextKey, ...modalContext } = context;

    if (headerTextKey) {
      modalContext.headerText = t(headerTextKey);
    }
    if (bodyTextKey) {
      modalContext.bodyText = t(bodyTextKey);
    }

    modal.open('authenticate', {
      signUpType: 'short',
      requireBusiness: requireBusinessOnSignup.value,
      headerText: t('order:modals.authenticate.header.saveYourWork'),
      bodyText: t('order:modals.authenticate.body.saveDraft'),
      ...modalContext,
    });
  }

  function onAuthenticateSuccess(user: Customer) {
    orderStore.currentOrder.customer = user;
    orderStore.currentOrder.customerId = user.id;

    if (onAfterAuthenticateSuccess.value) {
      return onAfterAuthenticateSuccess.value();
    }

    orderStore.populateAddresses();
  }

  function onAuthenticateDismiss() {
    modal.close('authenticate', { trigger: 'dismiss' });
    authenticationDismissed.value = true;
    if (orderStore.isClosing) return closeOrder();
  }

  function onAuthenticateDiscard(confirmDiscardKey?: string) {
    const onConfirm = () => {
      orderStore.isClosing = true;
      modal.close('authenticate', { trigger: 'discard' });
      return closeOrder();
    };

    modal.open('confirm', {
      body: confirmDiscardKey
        ? t(confirmDiscardKey)
        : t('order:modals.discard-order.confirmDiscard'),
      confirmColor: 'alert',
      onConfirm,
      onCancel: onAuthenticateDismiss,
    });
  }

  // Reset function for cleanup
  function reset() {
    authenticationDismissed.value = false;
    onAfterAuthenticateSuccess.value = null;
    prevRoute.value = null;
  }

  function closeOrder() {
    let name = prevRoute.value?.name || 'home';
    if (['/signin', '/'].includes(prevRoute.value?.path || '')) name = 'home';

    return router.push({ name });
  }

  function onCloseOrder() {
    if (activeUserStore.isLoggedIn) {
      return closeOrder();
    }

    showAuthenticateModal(
      {
        showDiscard: true,
        bodyTextKey: 'order:modals.authenticate.body.continueQuote',
      },
      closeOrder
    );
  }

  return {
    // State
    authenticationDismissed,
    onAfterAuthenticateSuccess,
    prevRoute,

    // Computed
    requireBusinessOnSignup,

    // Methods
    authenticateUserIfNecessary,
    showAuthenticateModal,
    onAuthenticateSuccess,
    onAuthenticateDismiss,
    onAuthenticateDiscard,
    reset,
    onCloseOrder,
    onSetupE2ETest() {
      if (navigator.userAgent.includes('e2e-test')) {
        // @ts-expect-error not going to type this on window
        window.E2E_TRIGGER_AUTH = this.authenticateUserIfNecessary;
      }
    },
  };
}
